/**
 * CanvasEnhancements.tsx
 * Améliorations du Canvas : Safe Zone, Smart Guides, Quick Actions
 */
import React, { useEffect, useState, useRef } from 'react';
import { Rect } from 'react-konva';
import { useAppStore } from '../../../services/StateService';

/**
 * Composant Safe Zone Indicator
 * CORRIGÉ: La Safe Zone est maintenant rendue directement dans Konva
 * pour qu'elle suive exactement le rectangle du template avec zoom/pan
 */

export function SafeZoneIndicator(): React.ReactElement | null {
  const showSafeZone = useAppStore(state => state.project.safeZoneVisible);
  const templateWidth = useAppStore(state => state.canvas.templateWidth);
  const templateHeight = useAppStore(state => state.canvas.templateHeight);
  const printSettings = useAppStore(state => state.project.printSettings);
  const shapes = useAppStore(state => state.shapes);

  if (!showSafeZone) return null;

  // Calculer les marges de la Safe Zone en pixels
  const safeZoneMm = printSettings?.safeZoneMm || 5;
  const dpi = useAppStore.getState().project.dpi;
  const safeZonePx = (safeZoneMm / 25.4) * dpi;

  // Vérifier si des éléments sont en dehors de la Safe Zone
  const hasElementsOutside = shapes.some(shape => {
    const x = shape.x || 0;
    const y = shape.y || 0;
    const width = shape.width || 0;
    const height = shape.height || 0;

    return (
      x < safeZonePx ||
      y < safeZonePx ||
      x + width > templateWidth - safeZonePx ||
      y + height > templateHeight - safeZonePx
    );
  });

  // CORRIGÉ: Rendre la Safe Zone directement dans Konva
  // Elle sera dans le même Group que le template (x: 50, y: 50)
  // et suivra automatiquement le zoom/pan du Stage
  return (
    <Rect
      x={safeZonePx}
      y={safeZonePx}
      width={templateWidth - safeZonePx * 2}
      height={templateHeight - safeZonePx * 2}
      stroke="#ff3b30"
      strokeWidth={2}
      dash={[8, 4]}
      fill="transparent"
      listening={false}
      name="safe-zone-indicator"
      opacity={hasElementsOutside ? 1 : 0.7}
    />
  );
}

/**
 * Composant Bleed Indicator (Fond perdu)
 * Affiche la zone de fond perdu qui sera coupée lors de l'impression
 */
export function BleedIndicator(): React.ReactElement | null {
  const showBleed = useAppStore(state => state.project.bleedVisible);
  const templateWidth = useAppStore(state => state.canvas.templateWidth);
  const templateHeight = useAppStore(state => state.canvas.templateHeight);
  const printSettings = useAppStore(state => state.project.printSettings);

  if (!showBleed) return null;

  // Calculer les marges du fond perdu en pixels
  const bleedMm = printSettings?.bleedMm || 0.5;
  const dpi = useAppStore.getState().project.dpi;
  const bleedPx = (bleedMm / 25.4) * dpi;

  // CORRIGÉ: Rendre le fond perdu directement dans Konva
  // Le fond perdu s'étend AU-DELÀ du template (négatif)
  return (
    <Rect
      x={-bleedPx}
      y={-bleedPx}
      width={templateWidth + bleedPx * 2}
      height={templateHeight + bleedPx * 2}
      stroke="#ef4444"
      strokeWidth={1.5}
      dash={[5, 5]}
      fill="transparent"
      listening={false}
      name="bleed-indicator"
      opacity={0.6}
    />
  );
}

/**
 * Composant Quick Actions Menu (Menu flottant)
 */
export function QuickActionsMenu({
  shapeId,
  position,
}: {
  shapeId: string;
  position: { x: number; y: number };
}): React.ReactElement {
  const deleteShape = useAppStore(state => state.deleteShape);
  const duplicateShape = useAppStore(state => state.duplicateShape);
  const shapes = useAppStore(state => state.shapes);

  const shape = shapes.find(s => s.id === shapeId);
  if (!shape) return <></>;

  const handleDuplicate = () => {
    duplicateShape(shapeId);
  };

  const handleDelete = () => {
    deleteShape(shapeId);
  };

  const style: React.CSSProperties = {
    position: 'absolute',
    left: `${position.x}px`,
    top: `${position.y}px`,
    transform: 'translate(-50%, calc(100% + 8px))',
  };

  return (
    <div className="quick-actions-menu" style={style}>
      <button
        className="quick-action-btn"
        onClick={handleDuplicate}
        title="Dupliquer"
        aria-label="Dupliquer l'élément"
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
          <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
        </svg>
      </button>
      <button
        className="quick-action-btn"
        onClick={handleDelete}
        title="Supprimer"
        aria-label="Supprimer l'élément"
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <polyline points="3 6 5 6 21 6" />
          <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
        </svg>
      </button>
      <button className="quick-action-btn" title="Plus d'options" aria-label="Plus d'options">
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <circle cx="12" cy="12" r="1" />
          <circle cx="19" cy="12" r="1" />
          <circle cx="5" cy="12" r="1" />
        </svg>
      </button>
    </div>
  );
}

/**
 * Hook pour gérer l'affichage du Quick Actions Menu
 */
export function useQuickActions(selectedNodeId: string | null) {
  const [showQuickActions, setShowQuickActions] = useState(false);
  const [quickActionsPosition, setQuickActionsPosition] = useState({ x: 0, y: 0 });
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (selectedNodeId) {
      // Afficher le menu après un court délai
      timeoutRef.current = setTimeout(() => {
        setShowQuickActions(true);
        // Positionner le menu sous l'élément sélectionné
        // TODO: Calculer la position réelle depuis le canvas
        setQuickActionsPosition({ x: 400, y: 300 });
      }, 300);
    } else {
      setShowQuickActions(false);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [selectedNodeId]);

  return { showQuickActions, quickActionsPosition };
}

/**
 * Composant Smart Guide (Ligne d'alignement)
 */
export function SmartGuide({
  type,
  position,
}: {
  type: 'horizontal' | 'vertical';
  position: number;
}): React.ReactElement {
  const style: React.CSSProperties = {
    position: 'absolute',
    background: '#ff0080',
    zIndex: 100,
    pointerEvents: 'none',
    opacity: 0.8,
  };

  if (type === 'horizontal') {
    style.top = `${position}px`;
    style.left = '0';
    style.right = '0';
    style.height = '1px';
  } else {
    style.left = `${position}px`;
    style.top = '0';
    style.bottom = '0';
    style.width = '1px';
  }

  return <div className={`smart-guide ${type}`} style={style} />;
}
